import { addWeeks } from "date-fns"
import { useMemo } from "react"

import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { api } from "~/utils/api"
import { ChartType } from "~/constants/graphs"
import { useChartSeriesConfig } from "~/constants/colors"
import { dateSeries } from "~/utils/graphUtils"
import { roundForecast } from "~/utils/formatting"

type Props = {
  variantIds: string[]
  startDate: Date
  warehouseId?: string
  customSeasonalityKey?: string
  targetQuantity?: number
}

export default function NpiGraph({
  variantIds,
  startDate,
  customSeasonalityKey,
  targetQuantity,
}: Props) {
  const chartConfig = useChartSeriesConfig()
  const { data: npiForecast } = api.npi.forecasts.useQuery(
    { variantIds, startDate, customSeasonalityKey },
    { enabled: variantIds?.length > 0 },
  )

  const npiForecastSeries: DateChartSeries = useMemo(() => {
    const rawForecast = npiForecast ?? []

    // Calculate scaling factor if targetQuantity is provided
    let scaledData = rawForecast
    if (targetQuantity !== undefined && rawForecast.length > 0) {
      const totalForecast = rawForecast.reduce((sum, val) => sum + val.value, 0)
      const scalingFactor = totalForecast > 0 ? targetQuantity / totalForecast : 1
      scaledData = rawForecast.map((val) => ({
        ...val,
        value: val.value * scalingFactor,
      }))
    }

    return dateSeries(chartConfig.demand, {
      name: "NPI Forecast",
      type: "spline",
      data: scaledData.map((value) => ({
        value: roundForecast(value.value),
        milliseconds: value.date.getTime(),
      })),
    })
  }, [npiForecast, targetQuantity, chartConfig])

  return (
    <figure className="relative py-4">
      <SuperChart
        type={ChartType.TimeSeries}
        height={250}
        data={[npiForecastSeries]}
        dateRange={{ start: startDate, end: addWeeks(startDate, 51) }}
      />
    </figure>
  )
}
